import React, { createContext, useState, useContext } from "react";
import {
  Dialog,
  DialogActions,
  Slide,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import { pageScrollStyle } from "../styles/styles";

const TransitionSlide = React.forwardRef((props, ref) => (
  <Slide direction="down" ref={ref} {...props} />
));

const DialogContext = createContext();

export const DialogProvider = ({ children }) => {
  const [dialogs, setDialogs] = useState([]);

  const showDialog = (content, props = {}) => {
    const dialogId = Date.now().toString();
    setDialogs((currentDialogs) => [
      ...currentDialogs,
      {
        id: dialogId,
        content,
        props,
        isClosing: false,
      },
    ]);
    return dialogId;
  };

  const closeDialog = (dialogId) => {
    if (dialogId) {
      // Mark the dialog as closing but keep it in the array
      setDialogs((currentDialogs) =>
        currentDialogs.map((dialog) =>
          dialog.id === dialogId ? { ...dialog, isClosing: true } : dialog
        )
      );
    } else {
      // If no dialogId provided, close the last one
      setDialogs((currentDialogs) => {
        const newDialogs = [...currentDialogs];
        if (newDialogs.length > 0) {
          const lastIndex = newDialogs.length - 1;
          newDialogs[lastIndex] = { ...newDialogs[lastIndex], isClosing: true };
        }
        return newDialogs;
      });
    }
  };

  // Function to actually remove dialog from state after animation completes
  const removeDialog = (dialogId) => {
    setDialogs((currentDialogs) =>
      currentDialogs.filter((dialog) => dialog.id !== dialogId)
    );
  };

  // Close all dialogs
  const closeAllDialogs = () => {
    setDialogs((currentDialogs) =>
      currentDialogs.map((dialog) => ({ ...dialog, isClosing: true }))
    );
  };

  return (
    <DialogContext.Provider
      value={{ showDialog, closeDialog, closeAllDialogs }}
    >
      {children}

      {/* Render each dialog in the stack */}
      {dialogs?.map((dialog, index) => {
        // Extract custom props that shouldn't be passed to DOM elements
        const {
          dialogTitle,
          formType,
          actions,
          width,
          fullScreen,
          transition,
          disableBackdropClose,
          ...otherProps
        } = dialog?.props || {};

        return (
          <Dialog
            key={dialog?.id}
            className="no-tooltip"
            open={!dialog.isClosing}
            onClose={
              disableBackdropClose ? undefined : () => closeDialog(dialog?.id)
            }
            fullScreen={fullScreen || false}
            TransitionComponent={transition || TransitionSlide}
            // Control z-index for stacking dialogs properly
            sx={{ zIndex: 1300 + index }}
            // Handle exit animation completion
            TransitionProps={{
              onExited: () => removeDialog(dialog.id),
            }}
            PaperProps={{
              sx: {
                width: width ? width : "80%",
                maxWidth: width ? width : null,
                padding: 0,
                display: "flex",
                flexDirection: "column",
                ...pageScrollStyle,
              },
            }}
            {...otherProps} // Safe props that can be passed to Dialog
          >
            <Box>
              {formType !== "matter" && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: "0.5rem",
                    py: "0.2rem",
                    bgcolor: "primary.main",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "start",
                      alignItems: "center",
                      gap: "1rem",
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="white.main"
                      sx={{
                        fontSize: "1rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      // Using the extracted dialogTitle prop here
                    >
                      {dialogTitle}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: "1rem" }}>
                    <IconButton
                      edge="start"
                      color="white"
                      size="small"
                      aria-label="close"
                      onClick={() => closeDialog(dialog.id)}
                    >
                      <CloseRounded />
                    </IconButton>
                  </Box>
                </Box>
              )}
              <Box>{dialog.content}</Box>
              {actions && (
                <DialogActions
                  sx={{
                    borderTop: "1px solid #ddd",
                    position: "sticky",
                    bottom: 0,
                    backdropFilter: "blur(10px)",
                    display: "flex",
                    gap: "1rem",
                    zIndex: 1500,
                  }}
                >
                  {actions}
                </DialogActions>
              )}
            </Box>
          </Dialog>
        );
      })}
    </DialogContext.Provider>
  );
};

// Custom Hook for easier access
export const useDialog = () => useContext(DialogContext);
